// Import 3rd-party dependencies
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { SubSink } from 'subsink';
import WorldCities from 'worldcities';
import cityTimeZone from 'city-timezones';

// Import project-specific dependencies
import { ITimeZoneLocale } from '@app/services/user-profile/user-profile.service';
import { CLOCK_FAVORITES_TAB_COMPONENT_CONSTS } from '@app/config/constants';
import { Subject, timer } from 'rxjs';
import * as momentTz from 'moment-timezone';

export interface IClockFavoritesTabData {
  favorites: ITimeZoneLocale[];
  recents: ITimeZoneLocale[];
}

@Component({
  selector: 'app-clock-favorites-tab',
  templateUrl: './clock-favorites-tab.component.html',
  styleUrls: ['./clock-favorites-tab.component.css']
})
export class ClockFavoritesTabComponent implements OnInit, OnDestroy {
  // Class attributes
  private subs: SubSink;
  location: FormControl;
  locations: ITimeZoneLocale[];
  zeroQueryList: ITimeZoneLocale[] | null;
  optionsListLength: number;
  optionsListItemHeight: number;
  errorMessage$: Subject<string>;
  @Input() settings: IClockFavoritesTabData;
  @Output() changeSettings: EventEmitter<IClockFavoritesTabData>;

  consts = CLOCK_FAVORITES_TAB_COMPONENT_CONSTS;

  // Class methods
  constructor() {
    this.changeSettings = new EventEmitter<IClockFavoritesTabData>();
  }

  ngOnInit(): void {
    // Initialization
    this.location = new FormControl();
    this.locations = [];
    this.optionsListLength = this.consts.CLOCK_SEARCH_MAX_OPTION_LIST_ITEMS;
    this.optionsListItemHeight = this.consts.CLOCK_SEARCH_OPTION_LIST_ITEM_HEIGHT;
    this.errorMessage$ = new Subject<string>();
    this.subs = new SubSink();

    this.setZeroQueryList(true);
  }

  ngOnDestroy(): void {
    this.errorMessage$.next(null);
    this.errorMessage$.complete();
    this.subs.unsubscribe();
  }

  onSettingsChanged(): void {
    this.changeSettings.emit(this.settings);
  }

  drop(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.settings.favorites, event.previousIndex, event.currentIndex);
    this.onSettingsChanged();
  }

  getLocationIndexInList(list: ITimeZoneLocale[], location: ITimeZoneLocale): number {
    return list
      .map((location) => {
        return `${location.city}, ${location.province ? location.province + ', ' : ''}${location.country}`;
      })
      .indexOf(
        location ? `${location.city}, ${location.province ? location.province + ', ' : ''}${location.country}` : ''
      );
  }

  setZeroQueryList(isZeroQuerySearch: boolean): void {
    this.zeroQueryList = isZeroQuerySearch
      ? this.settings.recents
          .filter((recent) => this.getLocationIndexInList(this.settings.favorites, recent) < 0)
          .slice(0, this.consts.CLOCK_SEARCH_MAX_OPTION_LIST_ITEMS)
      : null;
  }

  getTimezoneOffset(timezone: string): string {
    const tzInfo = timezone ? momentTz.utc().clone().tz(timezone).toString().split(' ').splice(-1)[0].replace('GMT', 'UTC') : null;
    return tzInfo?"(".concat(tzInfo.substring(0,4) + tzInfo.substring(4,6).concat(":")+ tzInfo.substring(6, 8)).concat(")"):''  
  }

  searchCountry(term: string): void {
    this.setZeroQueryList(false);
    this.errorMessage$.next(null);
    const locations = cityTimeZone.findFromCityStateProvince(term);
    const findUnique = ((arr: Array<ITimeZoneLocale>, predicate: any) : Array<ITimeZoneLocale> => {
      const found = Array<ITimeZoneLocale>();
      arr.forEach((d:ITimeZoneLocale) => {
        found[predicate(d)] = d;
      });
      return Object.keys(found).map(key => found[key]); 
    })
    
    this.locations = 
      this.locations = 
        locations
          .map((location) => {
            return {
              city: location.city,
              province: this.getTimezoneOffset(location.timezone),
              country: location.country,
              timezone: location.timezone,
            } as ITimeZoneLocale;
          })
          .filter((location) => location.timezone != null)
        }

    

  setRecentSearch(location: ITimeZoneLocale): void {
    if (location) {
      const timezone = location.timezone;
      const recent: ITimeZoneLocale = {
        city: location.city,
        province: location.province,
        country: location.country,
        timezone: timezone
      };

      // Add to recents
      const index = this.getLocationIndexInList(this.settings.recents, recent);
      if (index > -1) {
        // Remove existing recent search
        this.settings.recents.splice(index, 1);
      } else if (this.settings.recents.length >= this.consts.CLOCK_SETTINGS_MAX_RECENT_SEARCH_LENGTH) {
        // Remove last recent
        this.settings.recents.pop();
      }
      this.settings.recents.unshift(recent);

      // Add to favorites
      if (this.getLocationIndexInList(this.settings.favorites, this.settings.recents[0]) < 0) {
        this.errorMessage$.next(null);
        if (this.settings.favorites.length < this.consts.CLOCK_SETTINGS_MAX_FAVORITES_LENGTH) {
          this.settings.favorites.push(this.settings.recents[0]);
        } else {
          this.errorMessage$.next(this.consts.CLOCK_SETTINGS_ERROR_MAX_FAVORITES_REACHED);
          this.subs.sink = timer(this.consts.STATUS_TIMEOUT).subscribe(
            () => this.errorMessage$.next(null)
          );
        }
      }
      this.onSettingsChanged();

      // Reset text box content
      this.location.setValue(null);
      this.locations = [];
      this.setZeroQueryList(true);
    }
  }

  removeFavorite(index: number): void {
    if (index >= 0 && index < this.settings.favorites.length) {
      this.settings.favorites.splice(index, 1)[0];
      this.onSettingsChanged();
    }

    if (this.settings.favorites.length < this.consts.CLOCK_SETTINGS_MAX_FAVORITES_LENGTH) {
      this.errorMessage$.next(null);
    }
  }
}
